import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { 
  collection, addDoc, onSnapshot, query, 
  where, doc, updateDoc, deleteDoc, Timestamp, orderBy 
} from 'firebase/firestore';
import Swal from 'sweetalert2';

function Expensehead() {
  const [heads, setHeads] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Modal & Form State
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [headName, setHeadName] = useState("");

  const userId = auth.currentUser?.uid;
  const Toast = Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });

  // 1. Fetch Expense Heads
  useEffect(() => {
    if (!userId) return;
    const q = query(
      collection(db, "expense_heads"), 
      where("userId", "==", userId),
      orderBy("createdAt", "desc")
    );
    
    const unsub = onSnapshot(q, (snap) => {
      setHeads(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [userId]);

  const openModal = (head = null) => {
    if (head) {
      setEditId(head.id);
      setHeadName(head.headName);
    } else {
      setEditId(null);
      setHeadName("");
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!headName.trim()) return;

    setLoading(true);
    try {
      if (editId) {
        // UPDATE
        await updateDoc(doc(db, "expense_heads", editId), {
          headName: headName.trim(),
          updatedAt: Timestamp.now()
        });
        Toast.fire({ icon: 'success', title: 'Category Updated' });
      } else {
        // ADD
        await addDoc(collection(db, "expense_heads"), {
          userId,
          headName: headName.trim(),
          createdAt: Timestamp.now()
        });
        Toast.fire({ icon: 'success', title: 'Category Added' });
      }
      setShowModal(false);
    } catch (err) {
      Toast.fire({ icon: 'error', title: err.message });
    } finally {
      setLoading(false);
    }
  };

  const deleteHead = async (id) => {
    const confirm = await Swal.fire({
      title: 'Are you sure?',
      text: "Deleting this head won't delete past expenses, but it's recommended to keep it if in use.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
    });

    if (confirm.isConfirmed) {
      try {
        await deleteDoc(doc(db, "expense_heads", id));
        Toast.fire({ icon: 'success', title: 'Deleted Successfully' });
      } catch (err) {
        Toast.fire({ icon: 'error', title: 'Delete failed' });
      }
    }
  };

  return (
    <div className="content-wrapper bg-light min-vh-100">
      <section className="content-header py-3 px-4">
        <div className="d-flex justify-content-between align-items-center bg-white p-3 shadow-sm rounded border-left-info">
          <div>
            <h4 className="m-0 font-weight-bold text-navy">
              <i className="fas fa-tags mr-2 text-info"></i>Expense Categories
            </h4>
            <small className="text-muted">Manage your pump expense heads (e.g. Electricity, Staff Tea)</small>
          </div>
          <button className="btn btn-info shadow-sm px-4" onClick={() => openModal()}>
            <i className="fas fa-plus mr-1"></i> Add New Head
          </button>
        </div>
      </section>

      <section className="content px-4">
        <div className="card border-0 shadow-sm rounded-lg">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="bg-light text-muted small uppercase font-weight-bold">
                  <tr>
                    <th width="80" className="text-center">Sr. No</th>
                    <th>Category / Head Name</th>
                    <th width="150" className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {heads.length > 0 ? heads.map((h, i) => (
                    <tr key={h.id}>
                      <td className="text-center font-weight-bold text-secondary">{i + 1}</td>
                      <td className="font-weight-bold text-dark">{h.headName}</td>
                      <td className="text-center">
                        <button className="btn btn-sm text-primary mr-3" onClick={() => openModal(h)}>
                          <i className="fas fa-edit"></i>
                        </button>
                        <button className="btn btn-sm text-danger" onClick={() => deleteHead(h.id)}>
                          <i className="fas fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="3" className="text-center py-5 text-muted">
                        No expense heads found. Click "Add New Head" to start.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* REACT MODAL */}
      {showModal && (
        <div className="custom-modal-overlay">
          <div className="custom-modal-content animate__animated animate__zoomIn">
            <div className="modal-header bg-navy text-white py-2">
              <h6 className="modal-title">{editId ? 'Edit Category' : 'Add New Category'}</h6>
              <button className="close text-white" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="p-4">
                <div className="form-group mb-0">
                  <label className="small font-weight-bold">Category Name</label>
                  <input 
                    type="text" 
                    className="form-control form-control-lg border-primary" 
                    placeholder="e.g. Electricity Bill"
                    value={headName}
                    onChange={(e) => setHeadName(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
              </div>
              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" disabled={loading} className="btn btn-info btn-sm px-4">
                  {loading ? 'Processing...' : editId ? 'Update Category' : 'Save Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style dangerouslySetInnerHTML={{ __html: `
        .text-navy { color: #0f172a !important; }
        .bg-navy { background: #0f172a !important; }
        .border-left-info { border-left: 5px solid #17a2b8; }
        .custom-modal-overlay {
          position: fixed; top: 0; left: 0; width: 100%; height: 100%;
          background: rgba(15, 23, 42, 0.7); display: flex; align-items: center; justify-content: center; z-index: 1050;
        }
        .custom-modal-content {
          background: white; width: 400px; border-radius: 12px; overflow: hidden; box-shadow: 0 20px 40px rgba(0,0,0,0.4);
        }
      `}} />
    </div>
  );
}

export default Expensehead;